/**
 * [C34] D-308: the watchdog's hypotheses of one stored difference (`GET /cash-counts/:id/hypotheses`) turned into the
 * approve / reject dialogs' list — sentence key + formatted parameters, the arithmetic with this office's number
 * format, each operation's journal code, type and time (office time zone). Refetched every few seconds while the
 * watchdog has not analysed the count yet (it is woken by the count itself, so this is usually immediate).
 */
import { useQuery } from "@tanstack/react-query";
import type { HypothesesState, HypothesisItem } from "@faylasuf/ui";
import type { CountHypothesesView } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { fmt } from "../customers/format.js";

const FEE_KEY: Record<string, string> = { profit: "mt.hyp.fee_profit", loss: "mt.hyp.fee_loss", expense: "mt.hyp.fee_expense", partner_debt: "mt.hyp.fee_partner", partner_trust: "mt.hyp.fee_partner" };

/** Formats every number of a formula (`|d| = 0.9 × t0` → `900,000 = 0.9 × 1,000,000`). */
function arithmetic(formula: string, magnitude: string, terms: ReadonlyArray<{ amount: string; signed?: string }>, usd: boolean): string {
  const money = (v: string) => (usd ? `$${fmt(v)}` : fmt(v));
  let s = formula
    .replace("|t1 − t0|", `|${terms[1]?.signed ?? terms[1]?.amount ?? ""} − (${terms[0]?.signed ?? terms[0]?.amount ?? ""})|`)
    .replace(/\|d\|/g, "§D");
  s = s.replace(/t(\d)/g, (_, i: string) => `§T${i}`);
  // multipliers / note counts / denominations stay plain numbers with digit grouping
  s = s.replace(/(?<![§\d.])(\d+(?:\.\d+)?)/g, (m) => (m.includes(".") ? m : fmt(m)));
  s = s.replace(/§D/g, money(magnitude)).replace(/§T(\d)/g, (_, i: string) => money(terms[Number(i)]?.amount ?? "0"));
  return s.replace(/\|(-?[\d.]+) − \((-?[\d.]+)\)\|/, (_, a: string, b: string) => `|${money(a)} − (${money(b)})|`);
}

export function useHypotheses(countId: string | null): HypothesesState | null {
  const { source, t, locale } = useApp();
  const { session } = useSession();
  const q = useQuery({
    queryKey: ["cash-count-hypotheses", countId],
    queryFn: () => source.countHypotheses(countId!),
    enabled: countId !== null,
    refetchInterval: (query) => (query.state.data && !(query.state.data as CountHypothesesView).analysed && !(query.state.data as CountHypothesesView).failed ? 3_000 : false),
    staleTime: 2_000,
  });
  if (countId === null) return null;
  const v = q.data;
  if (!v) return { status: q.isError ? "failed" : "loading", items: [] };
  if (v.failed) return { status: "failed", items: [] };
  if (!v.analysed) return { status: "pending", items: [] };
  const usd = v.currency === "USD";
  const money = (x: string) => (usd ? `$${fmt(x)}` : `${fmt(x)} ${t("mt.iqd_word")}`);
  const tz = session.office.timeZone;
  const time = (iso: string | undefined) => (iso ? new Date(iso).toLocaleString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "");
  const items: HypothesisItem[] = v.hypotheses.map((h) => {
    const op0 = h.operations[0];
    const op1 = h.operations[1];
    const t0 = h.terms[0];
    const params: Record<string, string> = {
      amount: money(t0?.amount ?? h.magnitude),
      code: op0?.code ?? "",
      code2: op1?.code ?? "",
    };
    let key = `mt.hyp.${h.rule}`;
    switch (h.rule) {
      case "single_cash_leg":
        key = h.consistent === false ? "mt.hyp.single_opposite" : h.params["direction"] === "out" ? "mt.hyp.single_out" : "mt.hyp.single_in";
        break;
      case "fee_line":
        key = FEE_KEY[h.params["line"] ?? "profit"] ?? "mt.hyp.fee_profit";
        break;
      case "zero_error":
        key = h.params["tooLarge"] === "1" ? "mt.hyp.zero_large" : "mt.hyp.zero_small";
        params["typed"] = money(h.params["typed"] ?? "0");
        params["intended"] = money(h.params["intended"] ?? "0");
        break;
      case "denomination":
      case "bundle":
        params["notes"] = fmt(h.params["notes"] ?? "0");
        params["denomination"] = usd ? `$${fmt(h.params["denomination"] ?? "0")}` : fmt(h.params["denomination"] ?? "0");
        break;
      case "duplicate":
        params["minutes"] = h.params["minutes"] ?? "0";
        break;
      case "other_custody":
        if (h.params["onThisDrawer"] === "1") {
          key = "mt.hyp.other_custody_here";
          params["actor"] = h.params["actorName"] || t("mt.hyp.other_holder");
        } else params["holder"] = h.params["holderName"] || t("mt.hyp.other_holder");
        break;
      case "edited":
        params["before"] = money((h.terms[0]?.signed ?? "0").replace(/^-/, ""));
        params["after"] = money((h.terms[1]?.signed ?? "0").replace(/^-/, ""));
        params["code"] = op1?.code ?? op0?.code ?? "";
        break;
      case "corrections":
        params["count"] = h.params["count"] ?? "0";
        break;
    }
    return {
      rank: h.rank,
      grade: h.grade,
      sentenceKey: key,
      params,
      arithmetic: arithmetic(h.formula, h.magnitude, h.terms, usd),
      operations: h.operations.map((o) => ({ id: o.id, code: o.code ?? "", label: o.type ? t(`jr.type.${o.type}`) : "", time: time(o.postedAt), cancelled: o.cancelled === true })),
    };
  });
  return { status: items.length === 0 ? "none" : "ready", items };
}
